// this might be a server component but this depends on useFiltertimeControl and somewhere depends on useCookieConsentCategoryManager
'use client';

import { BypassBlock } from '@packages/shared/src/ui/BypassBlock/BypassBlock';
import { ConditionalWrapper } from '@packages/shared/src/ui/ConditionalWrapper/ConditionalWrapper';
import { cn } from '@packages/shared/src/utils/cn/cn';
import type { Alignment, Distance } from '../../../interfaces';
import { ComponentTypes } from '../../../interfaces';
import { useContentFilters } from '../../hooks/useContentFilters';
import { ComponentRecognizer } from '../ComponentRecognizer/ComponentRecognizer.client';
import { registerComponents } from '../ComponentRecognizer/componentRegistry';
import type { CmsGroupProps } from './types';

const mapVerticalAlignment = (type: Alignment) => {
  if (type === 'top') {
    return 'flex-start';
  }
  if (type === 'bottom') {
    return 'flex-end';
  }
  return type;
};

const mapLegacyColumns = (columns: number) => {
  switch (columns) {
    case 1:
      return 'grid-cols-1';
    case 3:
      return 'grid-cols-3';
    case 4:
      return 'grid-cols-4';
    case 5:
      return 'grid-cols-5';
    case 6:
      return 'grid-cols-6';
    default: // 2 columns by default
      return 'grid-cols-2';
  }
};

const BypassWrapper = (byPassLabel?: string) =>
  // eslint-disable-next-line func-names
  function (ch: React.ReactNode) {
    return <BypassBlock buttonText={byPassLabel ?? ''}>{ch}</BypassBlock>;
  };

const DistanceWrapper = (distanceType?: Distance) =>
  // eslint-disable-next-line func-names
  function (ch: React.ReactNode) {
    return <div className={distanceType === 'variation-1' ? 'px-3 lg:px-0' : ''}>{ch}</div>;
  };

export const CmsGroup = ({ data, className }: CmsGroupProps) => {
  const {
    // listing all possible values for the mobile columns, so the tailwind compiler can pick them up
    // grid-cols-1 grid-cols-2 grid-cols-2_66_33 grid-cols-2_33_66 grid-cols-3 grid-cols-3_50_25_25 grid-cols-3_25_50_25 grid-cols-3_25_25_50 grid-cols-4 grid-cols-5 grid-cols-6
    behavior_paragraph_columns_layout_mobile: columnsMobile = '1',
    // listing all possible values for the desktop columns, so the tailwind compiler can pick them up
    // md:grid-cols-1 md:grid-cols-2 md:grid-cols-2_66_33 md:grid-cols-2_33_66 md:grid-cols-2 md:grid-cols-3_50_25_25 md:grid-cols-3_25_50_25 md:grid-cols-3_25_25_50 md:grid-cols-4 md:grid-cols-5 md:grid-cols-6
    behavior_paragraph_columns_layout_desktop: columnsDesktop = '1',
    field_cells_per_row: cellsPerRowDesktop = data.paragraph ? data.paragraph.length : 0,
    field_cells_per_row_mobile: cellsPerRowMobile = Math.floor(cellsPerRowDesktop / 2) || 1,
    field_col_gap: colGap = '0',
    field_row_gap: rowGap = '0',
    field_margin: margin = '0',
    field_padding: padding = '0',
    field_background_color: backgroundColor = 'transparent',
    field_alignment: horizontalAlignment = 'center',
    field_vertical_alignment: verticalAlignment = 'flex-start',
    field_custom_id: dynamicYieldId,
    field_distance: distanceType,
    field_skip_link_label: byPassLabel,
    behavior_paragraph_borderRadius: borderRadius,
    id,
  } = data;
  const filters = useContentFilters();
  const contentElements = data.paragraph?.filter((item) => filters.every((filter) => filter(item)));

  if (!contentElements?.length || contentElements.length === 0) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <></>;
  }

  return (
    <ConditionalWrapper condition={Boolean(byPassLabel)} wrapper={BypassWrapper(byPassLabel)}>
      <ConditionalWrapper
        condition={distanceType === 'fullwidth' || distanceType === 'variation-1'}
        wrapper={DistanceWrapper(distanceType)}
      >
        <div
          key={`group-${id}`}
          className={cn(
            className,
            '_c_cms-group',
            data.behavior_paragraph_columns_layout_mobile
              ? `grid-cols-${columnsMobile}`
              : mapLegacyColumns(cellsPerRowMobile),
            data.behavior_paragraph_columns_layout_desktop
              ? `md:grid-cols-${columnsDesktop}`
              : `md:${mapLegacyColumns(cellsPerRowDesktop)}`,
            horizontalAlignment === 'center' && 'text-center',
            horizontalAlignment === 'left' && 'text-left',
            horizontalAlignment === 'right' && 'text-right',
          )}
          style={{
            background: backgroundColor,
            borderRadius,
            margin,
            padding,
            justifyContent: horizontalAlignment,
            alignItems: mapVerticalAlignment(verticalAlignment),
            gap: `${rowGap} ${colGap}`,
          }}
          data-dy={dynamicYieldId}
        >
          {contentElements.map(
            (item) =>
              item && (
                <div
                  key={item.id}
                  className="[&_.\_c\_cms-group]:h-full [&_.\_c\_cms-group]:max-w-full [&_.\_c\_cms-group]:xl:container [&_.\_c\_cms-group]:xl:mx-auto!"
                >
                  <ComponentRecognizer item={item} />
                </div>
              ),
          )}
        </div>
      </ConditionalWrapper>
    </ConditionalWrapper>
  );
};

// Needed to run this component in Storybook and Jest tests. Register the component in a global componentRegistry object
registerComponents({
  [ComponentTypes.Group]: CmsGroup,
  [ComponentTypes.GroupLocalizable]: CmsGroup,
  [ComponentTypes.GroupApp]: CmsGroup,
});
